/**
 * Traffic distribution AI block (Voluum path panel).
 * Per-path scope: edits only the selected path.
 */

import type { FC } from 'react';
import type {
  VoluumAutoOptimizationMode,
  VoluumPathUpdate,
} from '@trafficpulse/shared';

import {
  getOffersCalculationMethod,
  setOffersCalculationMethod,
} from '@/features/flow-optimizer/tabs/standard/v2/utils/path-ai';
import { useTrackerAiHidden } from '@/features/flow-optimizer/tracker';

import PillSegmentRow from './PillSegmentRow';
import PathSettingsCalculationMethodSelect from './PathSettingsCalculationMethodSelect';
import { PATH_AUTO_MODE_OPTIONS } from './path-settings.constants';
import { PathSettingsSectionTitle } from './path-settings-layout';
import PathSettingsDistributionTop from './PathSettingsDistributionTop';

export interface PathSettingsDistributionProps {
  path: VoluumPathUpdate;
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void;
  compact?: boolean;
}

const PathSettingsDistribution: FC<PathSettingsDistributionProps> = ({
  path,
  onPatch,
  compact = false,
}) => {
  // In TrafficPulse Optimisation (preview) mode the tracker's optimization
  // controls are auto-managed, and a tracker without its own AI has none —
  // hide the mode + method (render-only).
  const hideAi = useTrackerAiHidden();
  return (
  <section className={compact ? 'space-y-2' : 'space-y-3'}>
    <PathSettingsSectionTitle
      dense={compact}
      hint="Voluum adjusts lander/offer weights using your selected method when enabled. Per-path setting."
    >
      Traffic distribution AI (offers & landers)
    </PathSettingsSectionTitle>
    <PathSettingsDistributionTop
      path={path}
      onPatch={onPatch}
      compact={compact}
    />
    {!hideAi ? (
    <>
    <PillSegmentRow<VoluumAutoOptimizationMode>
      aria-label="Traffic distribution mode"
      value={path.autoOptimizationMode ?? 'INDEPENDENT'}
      options={PATH_AUTO_MODE_OPTIONS}
      onChange={(v) =>
        onPatch((p) => {
          p.autoOptimizationMode = v;
        })
      }
      compact={compact}
    />
    <PathSettingsCalculationMethodSelect
      value={getOffersCalculationMethod(path)}
      compact={compact}
      className={compact ? undefined : '[&_select]:w-full [&_select]:max-w-xs'}
      onChange={(v) =>
        onPatch((p) => {
          setOffersCalculationMethod(p, v);
        })
      }
    />
    </>
    ) : null}
  </section>
  );
};

export default PathSettingsDistribution;
